import { derived, writable } from "svelte/store"
import {
    checkPermissions,
    requestNotificationsPermissions,
    getSetNotifications,
    changeExactNotificationSetting
} from "./cap-notification-manager"
import { isNative } from "@utils/underhood/browser"
import type { PermissionState } from "@capacitor/core"

export enum NotificationPermissions {
    UNNOTIFIABLE,
    /**
     * Needs prompting for android exact notifications, only will be
     * set after promptable (regular notifications) are set. This will
     * never appear on iOS
     */
    NEED_EXACT,
    GRANTED,
    DENIED,
    PROMPTABLE,
}

const translator : Record<PermissionState, NotificationPermissions> = {
    granted: NotificationPermissions.GRANTED,
    denied: NotificationPermissions.DENIED,
    prompt: NotificationPermissions.PROMPTABLE,
    "prompt-with-rationale": NotificationPermissions.PROMPTABLE,
} 

const mainNotificationsPermissions = writable<PermissionState | null>(null)
const exactAlarmPermissions = writable<PermissionState | null>(null)

/**
 * Allows us to override in console for debugging purposes
 */
const DEBUG_OVERRIDE_PERMISSION = writable<NotificationPermissions | null>(null)

// @ts-expect-error debug
globalThis.setPermissions = (perm : NotificationPermissions) => DEBUG_OVERRIDE_PERMISSION.set(perm)

/**
 * Get the current permissions for the device for notifications
 */
export const notificationPermissions = derived([mainNotificationsPermissions, exactAlarmPermissions, DEBUG_OVERRIDE_PERMISSION], ([notifications, exact_alarm, debug]) => {
    if (debug !== null) return debug
    if (!isNative) return NotificationPermissions.UNNOTIFIABLE
    
    // Loading state
    if (notifications === null || exact_alarm === null) return null

    if (notifications === "granted") {
        return exact_alarm === "granted" ? NotificationPermissions.GRANTED : NotificationPermissions.NEED_EXACT
    } else {
        return translator[notifications]
    }
})

// // @ts-expect-error it's totally fine
// globalThis.setNotificationPermissions = notificationPermissions.set

export async function requestNotificationPermissions() : Promise<PermissionState> {
    if (!isNative) {
        throw new Error("Attempting to request notification perm on non-native device")
    }
    const req = await requestNotificationsPermissions()
    mainNotificationsPermissions.set(req)
    return req
}

export async function requestExactAlarmPermissions() : Promise<PermissionState> {
    if (!isNative) {
        throw new Error("Attempting to request exact alarm perm on non-native device")
    }
    const req = await changeExactNotificationSetting()
    exactAlarmPermissions.set(req)
    return req
}


/**
 * Asks to update the
 */
export async function refreshNotificationPermissions() {
    if (!isNative) {
        console.log(`Refreshing notifications but on a non-native device - OK`)
        return
    }
    const {notifications, exact_alarm} = await checkPermissions()
    mainNotificationsPermissions.set(notifications)
    exactAlarmPermissions.set(exact_alarm)
    return {notifications, exact_alarm}
}

// Refresh notifications if granted
queueMicrotask(async () => {
    refreshNotificationPermissions()
})

notificationPermissions.subscribe(async (res) => {
    if (res === NotificationPermissions.GRANTED) {
        console.log("Permission status changed to GRANTED: ", await getSetNotifications())
    }
})

// @ts-expect-error ***
globalThis.refreshNotificationPermissions = refreshNotificationPermissions
